import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Hardscaping', href: '/services/hardscaping' },
  { label: 'About', href: '/about' },
  { label: 'Service Areas', href: '/locations/colorado-springs' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-400"
        style={{
          background: scrolled ? 'rgba(255,255,255,0.98)' : 'transparent',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.07)' : '1px solid rgba(255,255,255,0.1)',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 2px 30px rgba(0,0,0,0.07)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-[76px] flex items-center justify-between gap-6">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer">
              <div className="w-11 h-11 rounded-xl bg-forest overflow-hidden flex items-center justify-center p-1.5 shrink-0">
                <img
                  src="https://cqc-landscaping.com/wp-content/themes/land7/images/logo.jpg"
                  alt="CQC Landscaping"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className={`font-serif text-xl font-semibold leading-none tracking-tight transition-colors ${scrolled ? 'text-forest' : 'text-white'}`}>
                  CQC Landscaping
                </div>
                <div className={`font-sans text-[9px] tracking-[0.18em] uppercase mt-0.5 transition-colors ${scrolled ? 'text-charcoal-muted' : 'text-white/50'}`}>
                  Hayden, ID &amp; Spokane, WA
                </div>
              </div>
            </Link>

            {/* Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-sans text-sm font-medium px-4 py-2.5 rounded-full transition-all cursor-pointer ${
                    isActive(link.href)
                      ? scrolled ? 'text-gold bg-gold/8' : 'text-gold'
                      : scrolled
                        ? 'text-charcoal-light hover:text-forest hover:bg-cream'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTAs */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <a
                href="tel:+12086596067"
                className={`flex items-center gap-2 text-sm font-semibold rounded-full px-5 py-2.5 transition-all cursor-pointer whitespace-nowrap border ${
                  scrolled
                    ? 'border-forest/25 text-forest hover:bg-forest hover:text-white hover:border-forest'
                    : 'border-white/30 text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-phone-fill text-gold text-xs"></i>
                (208) 659-6067
              </a>
              <Link
                to="/#estimate"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-full px-5 py-2.5 transition-all cursor-pointer shadow-lg shadow-gold/25"
              >
                Free Estimate <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => {
                const next = !mobileOpen;
                setMobileOpen(next);
                document.body.style.overflow = next ? 'hidden' : '';
              }}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${scrolled ? 'hover:bg-cream text-charcoal-mid' : 'hover:bg-white/10 text-white'}`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 top-[76px] bg-white z-40 px-6 py-8 overflow-y-auto flex flex-col">
          <nav className="flex flex-col gap-1 mb-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-sans text-base font-medium px-5 py-4 rounded-xl transition-colors cursor-pointer border-b border-cream ${isActive(link.href) ? 'text-gold' : 'text-charcoal-mid hover:text-forest'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-3 mt-auto">
            <a href="tel:+12086596067"
              className="flex items-center justify-center gap-2 border-2 border-forest/30 text-forest font-semibold rounded-full py-4 text-sm cursor-pointer hover:bg-forest hover:text-white transition-all whitespace-nowrap">
              <i className="ri-phone-fill text-gold"></i>
              (208) 659-6067
            </a>
            <Link to="/#estimate"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-full py-4 transition-all cursor-pointer shadow-lg shadow-gold/20">
              Get Free Estimate <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
